import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  InvCreateRequest,
  InvUpdateRequest,
  InvListQuery,
  InvListResponse,
  InvResponse,
  InvReconciliationResponse,
} from '@produzionejb/types';
import { api } from './api-client';

const QK = {
  list: (q: Partial<InvListQuery>) => ['inv-documents', 'list', q] as const,
  one: (id: string) => ['inv-documents', 'one', id] as const,
  recon: (id: string) => ['inv-documents', 'reconciliation', id] as const,
};

function toQueryString(q: Partial<InvListQuery>): string {
  const params = new URLSearchParams();
  if (q.page) params.set('page', String(q.page));
  if (q.pageSize) params.set('pageSize', String(q.pageSize));
  if (q.status) params.set('status', q.status);
  if (q.periodId) params.set('periodId', q.periodId);
  if (q.workDateFrom) params.set('workDateFrom', new Date(q.workDateFrom).toISOString());
  if (q.workDateTo) params.set('workDateTo', new Date(q.workDateTo).toISOString());
  const s = params.toString();
  return s ? `?${s}` : '';
}

export function useInvList(query: Partial<InvListQuery>) {
  return useQuery({
    queryKey: QK.list(query),
    queryFn: () => api<InvListResponse>(`/api/documents/inv${toQueryString(query)}`),
    staleTime: 10_000,
  });
}

export function useInv(id: string | undefined) {
  return useQuery({
    queryKey: id ? QK.one(id) : ['inv-documents', 'one', 'NIL'],
    queryFn: () => api<InvResponse>(`/api/documents/inv/${id}`),
    enabled: !!id,
  });
}

export function useInvReconciliation(id: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: id ? QK.recon(id) : ['inv-documents', 'reconciliation', 'NIL'],
    queryFn: () => api<InvReconciliationResponse>(`/api/documents/inv/${id}/reconciliation`),
    enabled: !!id && enabled,
    staleTime: 5_000,
  });
}

export function useCreateInv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: InvCreateRequest) =>
      api<InvResponse>('/api/documents/inv', { method: 'POST', body: data }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inv-documents', 'list'] });
    },
  });
}

export function useUpdateInv(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: InvUpdateRequest) =>
      api<InvResponse>(`/api/documents/inv/${id}`, { method: 'PATCH', body: data }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['inv-documents', 'list'] });
      void qc.invalidateQueries({ queryKey: QK.recon(id) });
    },
  });
}

export function useCloseInv(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api<InvResponse>(`/api/documents/inv/${id}/close`, { method: 'POST', body: {} }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.one(id), updated);
      void qc.invalidateQueries({ queryKey: ['inv-documents', 'list'] });
    },
  });
}

export function useDeleteInv() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/api/documents/inv/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['inv-documents', 'list'] });
    },
  });
}
